import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { useEffect, useId, useRef, useState } from 'react';

export interface InfoHintProps {
  /** The term being explained. Only used to label the button for screen readers. */
  label: string;
  triggerLabel?: string;
  triggerContent?: ReactNode;
  portalClassName?: string;
  /** What the term means, in plain language. One or two short sentences. */
  text: string;
  /**
   * Which edge of the bubble lines up with the trigger. `end` for hints sitting
   * near the right edge of a container, where a left-anchored bubble would run
   * off the page. Chosen by the caller rather than measured at runtime: the
   * callers here are a fixed grid, so the edge tile is known at author time and
   * a measuring effect would be machinery bought for nothing.
   */
  align?: 'start' | 'end';
}

/**
 * The small circled "i" beside a term, with a plain-language explanation.
 *
 * Kyu, 2026-08-27: "Touches might sound jargon to Alex, or any people who are
 * not in the weeds" — the readers of this dashboard are not the people who
 * built it, and a label that is exact to us is opaque to them.
 *
 * Three ways in on purpose, because a hover-only tooltip is only a tooltip for
 * somebody holding a mouse:
 *   - hover, for the case Kyu described;
 *   - focus, so Tab reaches it;
 *   - click, which is the only one of the three that exists on a touchscreen.
 *
 * The pointerType check is what keeps click working on touch. A touch that
 * synthesises a pointerenter would open the bubble on hover and the click that
 * follows would immediately toggle it shut again, so the tap would look dead.
 */
export function InfoHint({
  label,
  text,
  align = 'start',
  triggerLabel,
  triggerContent,
  portalClassName,
}: InfoHintProps) {
  // Two independent reasons to be open, not one toggle.
  //
  // A single `open` boolean looked right and was wrong the first time it was
  // driven: with a mouse, the hover opens the bubble and the click that
  // follows toggles it straight back shut, so clicking the icon you can
  // already see appears to do nothing. Hover is transient and click is sticky,
  // which are different facts, so they get a flag each.
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const open = pinned || hovered;
  const bubbleId = useId();
  const pointerFocus = useRef(false);
  const rootRef = useRef<HTMLSpanElement | null>(null);

  const dismiss = () => {
    setPinned(false);
    setHovered(false);
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    function onPointerDown(event: PointerEvent) {
      const root = rootRef.current;
      if (root && event.target instanceof Node && !root.contains(event.target)) {
        setPinned(false);
        setHovered(false);
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const rect = rootRef.current?.getBoundingClientRect();
  const bubble = open ? (
    <span
      id={bubbleId}
      role="tooltip"
      className={`ui-hint-bubble ui-hint-bubble--${align} ${portalClassName ?? ''}`}
      style={
        portalClassName && rect
          ? {
              position: 'fixed',
              top: Math.min(rect.bottom + 8, window.innerHeight - 140),
              left: Math.max(16, Math.min(rect.left, window.innerWidth - 296)),
            }
          : undefined
      }
    >
      {text}
    </span>
  ) : null;
  return (
    <span
      ref={rootRef}
      className="ui-hint"
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') {
          setHovered(true);
        }
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') {
          setHovered(false);
        }
      }}
    >
      <button
        type="button"
        className="ui-hint-trigger"
        aria-label={triggerLabel ?? `What does ${label} mean?`}
        aria-expanded={open}
        aria-describedby={open ? bubbleId : undefined}
        onPointerDown={() => {
          pointerFocus.current = true;
        }}
        onClick={() => {
          pointerFocus.current = false;
          // Pins it, so the bubble survives the mouse moving off the icon —
          // which is what somebody who clicked rather than hovered wants, and
          // the only way in at all on a touchscreen.
          setPinned((wasPinned) => !wasPinned);
        }}
        onFocus={() => {
          if (!pointerFocus.current) setPinned(true);
        }}
        onBlur={() => {
          pointerFocus.current = false;
          setPinned(false);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && open) {
            // Stopped here so Escape closes the hint without also closing the
            // drawer or dialog the hint might be sitting inside.
            event.stopPropagation();
            dismiss();
          }
        }}
      >
        <span aria-hidden="true">{triggerContent ?? 'i'}</span>
      </button>
      {portalClassName ? createPortal(bubble, document.body) : bubble}
    </span>
  );
}
